import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button } from '@mui/material';
import { CreateProviderOfflinePeriodRequest } from '@provider-portal/common/api/gen/DeliveryProviderPortalAuthApi';
import { DateTime } from 'luxon';
import { OfflinePeriodAcceptedDateFormat } from 'widgets/OpeningTimes/OfflinePeriods/constants/OfflinePeriodAcceptedDateFormat';
import { OfflinePeriodsEvents } from 'widgets/OpeningTimes/OfflinePeriods/constants/OfflinePeriodsEvents';
import { CreateOfflinePeriodDialog } from 'widgets/OpeningTimes/OfflinePeriods/CreateOfflinePeriodDialog';
import { EmptyOfflinePeriodsList } from 'widgets/OpeningTimes/OfflinePeriods/EmptyOfflinePeriodsList';
import { OfflinePeriodsList } from 'widgets/OpeningTimes/OfflinePeriods/OfflinePeriodsList';

import { Plus } from '@bolteu/kalep-react-icons';

import { selectSelectedProvider } from 'rdx/account';
import {
  createOfflinePeriodAsyncAction,
  getOfflinePeriodsAsyncAction,
  selectOfflinePeriods,
  selectOfflinePeriodsRequestState,
} from 'rdx/offlinePeriods';

import { useCommonAppServices } from 'root/CommonAppServicesContext';

import { ContainerCard } from '../../../../../common/all/components/ContainerCard/ContainerCard';
import { useMount } from '../../../../../common/all/hooks/useMount';

export interface OfflinePeriodsProps extends ReturnType<typeof useProps> {}

const OfflinePeriods: React.FC<OfflinePeriodsProps> = memo(({ actions, offlinePeriods }) => {
  const { formatMessage } = useIntl();
  const providerId = useSelector(selectSelectedProvider)?.providerId || null;
  const { mixpanelService } = useCommonAppServices();
  const [isCreateOfflinePeriodOpen, setCreateOfflinePeriodOpen] = useState(false);

  const initOrderedOfflinePeriods = React.useCallback(() => {
    return offlinePeriods.sort(
      (period1, period2) =>
        DateTime.fromFormat(period1.start_date, OfflinePeriodAcceptedDateFormat).toMillis() -
        DateTime.fromFormat(period2.start_date, OfflinePeriodAcceptedDateFormat).toMillis(),
    );
  }, [offlinePeriods]);
  const [orderedOfflinePeriods, setOrderedOfflinePeriods] = useState(initOrderedOfflinePeriods);

  useMount(() => {
    actions.getOfflinePeriods();
  });

  useEffect(() => {
    setOrderedOfflinePeriods(
      offlinePeriods.sort(
        (period1, period2) =>
          DateTime.fromFormat(period1.start_date, OfflinePeriodAcceptedDateFormat).toMillis() -
          DateTime.fromFormat(period2.start_date, OfflinePeriodAcceptedDateFormat).toMillis(),
      ),
    );
  }, [offlinePeriods, setOrderedOfflinePeriods]);

  const openCreateOfflinePeriodDialog = useCallback(() => {
    setCreateOfflinePeriodOpen(true);
    mixpanelService.trackProviderEvent(OfflinePeriodsEvents.OFFLINE_PERIOD_ADD_BUTTON_CLICKED, {
      providerId,
    });
  }, [mixpanelService, providerId]);

  const closeCreateOfflinePeriodDialog = useCallback(() => {
    setCreateOfflinePeriodOpen(false);
  }, [setCreateOfflinePeriodOpen]);

  return (
    <ContainerCard
      title={formatMessage({ id: 'offlinePeriods.title' })}
      description={formatMessage({ id: 'offlinePeriods.description' })}
      width="336px"
    >
      {offlinePeriods.length > 0 ? (
        <OfflinePeriodsList offlinePeriods={orderedOfflinePeriods} />
      ) : (
        <EmptyOfflinePeriodsList />
      )}
      <Box mt={2}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={openCreateOfflinePeriodDialog}
        >
          <Plus /> {formatMessage({ id: 'offlinePeriods.addOfflinePeriod' })}
        </Button>
        {isCreateOfflinePeriodOpen && (
          <CreateOfflinePeriodDialog
            isOpen={isCreateOfflinePeriodOpen}
            onClose={closeCreateOfflinePeriodDialog}
          />
        )}
      </Box>
    </ContainerCard>
  );
});

function useActions() {
  const dispatch = useDispatch();
  return useMemo(
    () => ({
      getOfflinePeriods: () => dispatch(getOfflinePeriodsAsyncAction.request()),
      createOfflinePeriods: (newOfflinePeriod: CreateProviderOfflinePeriodRequest) =>
        dispatch(createOfflinePeriodAsyncAction.request(newOfflinePeriod)),
    }),
    [dispatch],
  );
}

function useProps() {
  const offlinePeriodsRequestState = useSelector(selectOfflinePeriodsRequestState);
  const offlinePeriods = useSelector(selectOfflinePeriods) || [];

  return {
    offlinePeriodsRequestState,
    offlinePeriods,
    actions: useActions(),
  };
}

export default () => <OfflinePeriods {...useProps()} />;
